import React, { useId, useState } from 'react';
import { useLingui } from '@lingui/react';

import FlatButton from '@rover/components/src/OverflowTextModal/FlatButton';
import TrustLogo from '@rover/icons/svg/trust-logo.svg';
import { Button, Heading, Paragraph } from '@rover/kibble/core';
import ScrollableModal from '@rover/kibble/patterns/ScrollableModal';
import { useApiCountryConfigurationCurrentRetrieve } from '@rover/rsdk/src/apiClient/latest/api-country-configuration-current-retrieve';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import {
  InfoCardLearnMoreClick,
  InfoCardLearnMoreGuaranteeClick,
  LearnMoreClick,
} from '../utils/analyticsEvents';
import {
  LEARN_MORE_BUTTON_TEXT,
  LEARN_MORE_HREF,
  LEARN_MORE_TEXT,
  MESSAGE_PHOTO_TEXT,
  MODAL_HEADING_TEXT,
  MODAL_SUB_HEADING_TEXT,
  MODAL_SUBMIT_BUTTON_TEXT,
  ROVER_GUARANTEE_TEXT,
  SCREENED_PET_SITTERS_TEXT,
  SUPPORT_TEXT,
} from '../utils/RoverProtectSignals.constants';
import { StyledAnchor } from '../utils/styles';

type ModalContentProps = {
  isOpen: boolean;
  closeModal: () => void;
};

const ModalContent = ({ isOpen, closeModal }: ModalContentProps): JSX.Element => {
  const { i18n } = useLingui();
  const { data: countryConfig } = useApiCountryConfigurationCurrentRetrieve({
    query: { staleTime: Infinity },
  });
  const headerId = useId();

  return (
    <ScrollableModal
      isOpen={isOpen}
      onClose={closeModal}
      headerId={headerId}
      showCloseIcon
      showCloseIconMobile
      isDismissable={false}
      isSubmittable
      submitButtonText={i18n._(MODAL_SUBMIT_BUTTON_TEXT)}
      onSubmit={closeModal}
      footerContent={
        <Button
          href={LEARN_MORE_HREF}
          webProps={{ hrefTarget: '_blank' }}
          variant="flat"
          mt="3x"
          fullWidth
          onPress={() => emitAnalyticsEvent(new InfoCardLearnMoreClick())}
        >
          {i18n._(LEARN_MORE_BUTTON_TEXT)}
        </Button>
      }
    >
      <TrustLogo width={36} height={48} />
      <Heading id={headerId} size="300" mt="4x" fontWeight="semibold">
        {i18n._(MODAL_HEADING_TEXT)}
      </Heading>
      <Paragraph>{MODAL_SUB_HEADING_TEXT()}</Paragraph>
      <Paragraph>{SCREENED_PET_SITTERS_TEXT()}</Paragraph>
      <Paragraph>{MESSAGE_PHOTO_TEXT()}</Paragraph>
      <Paragraph>
        {ROVER_GUARANTEE_TEXT(countryConfig?.formattedVetCareReimbursementAmount || '$25,000')}{' '}
        <StyledAnchor
          href={countryConfig?.roverGuaranteeUrl || '/rover-guarantee'}
          target="_blank"
          onClick={() => emitAnalyticsEvent(new InfoCardLearnMoreGuaranteeClick())}
        >
          {i18n._(LEARN_MORE_TEXT)}
        </StyledAnchor>
      </Paragraph>
      <Paragraph mb="0x">{SUPPORT_TEXT()}</Paragraph>
    </ScrollableModal>
  );
};

const RoverProtectModal = (): JSX.Element => {
  const { i18n } = useLingui();
  const [isOpen, setIsOpen] = useState(false);

  const closeModal = (): void => setIsOpen(false);
  const handleOnClick = (): void => {
    setIsOpen(true);
    emitAnalyticsEvent(new LearnMoreClick());
  };

  return (
    <>
      <FlatButton onClick={handleOnClick} size="100" sx={{ fontWeight: 'regular' }}>
        {i18n._(LEARN_MORE_TEXT)}
      </FlatButton>
      <ModalContent isOpen={isOpen} closeModal={closeModal} />
    </>
  );
};

export default RoverProtectModal;
